import type { ForethoughtSendConversationAlertEvent } from 'embed/embed';
import type {
  ForethoughtAction,
  ForethoughtData,
  ForethoughtSelector,
  ProactiveTriggerEvent,
} from 'embed/forethought';
import { useEffect } from 'react';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

/**
 * A custom hook used to setup window.Forethought at the app level.
 *
 * Use window.postMessage where possible so that the same listeners that
 * would handle parent page window.Forethought calls handle these
 * as well.
 *
 * This logic mirrors that found in embed.ts except for it does not apply logic
 * that only applies to the iframe.
 */
export const useWindowForethought = () => {
  useEffect(() => {
    window.Forethought = <K extends ForethoughtAction>(
      selector: ForethoughtSelector,
      action: K,
      data?: ForethoughtData<K>,
    ) => {
      if (selector !== 'widget') return;

      // eslint-disable-next-line no-console
      console.log('Forethought action called:', { action, data, selector });

      if (action === 'open') {
        window.postMessage({
          action: 'openWidget',
          mechanism: 'js-api-widget-open',
        });
      }

      if (action === 'close') {
        window.postMessage({ action: 'closeWidget' });
      }

      if (action === 'hide') {
        // not applicable
      }

      if (action === 'show') {
        // not applicable
      }

      if (action === 'triggerEventComplete' && data) {
        const triggerEventData: ForethoughtData<'triggerEventComplete'> = data;
        const identifier = triggerEventData.identifier;
        const payload = triggerEventData.payload || {};

        // dont update conversation if no identifier is passed in
        if (!identifier) {
          const description = 'Event name is required at data.identifier';
          const alertMessage: ForethoughtSendConversationAlertEvent['data'] = {
            event: 'forethoughtSendConversationAlert',
            requestData: {
              alert_type: 'trigger-event-step',
              description: description,
              priority: 'P4',
              teams: ['Customer NOC Alerts'],
            },
          };
          window.postMessage(alertMessage);
          console.error(`TRIGGER EVENT ERROR - ${description}`);
        } else {
          window.postMessage({
            event: action,
            identifier,
            payload,
          });
        }
      }

      if (action === 'clearLocalData') {
        window.postMessage({ action: 'clearLocalData' });
      }

      if (action === 'launchQuery' && data) {
        const launchQueryData = data as ForethoughtData<'launchQuery'>;
        if (typeof launchQueryData?.payload?.query !== 'string') {
          sendErrorToSentry({
            consoleError: true,
            error:
              'LAUNCH QUERY ERROR - data param must be initial query string',
          });
        } else if (!launchQueryData.payload.query.trim()) {
          sendErrorToSentry({
            consoleError: true,
            error:
              'LAUNCH QUERY ERROR - data param must contain non whitespace characters',
          });
        } else {
          window.postMessage({
            action: 'launchQuery',
            payload: launchQueryData.payload,
          });
        }
      }

      if (action === 'showProactivePrompt') {
        window.postMessage({ action: 'showProactivePrompt' });
      }

      if (action === 'hideProactivePrompt') {
        window.postMessage({ action: 'hideProactivePrompt' });
      }

      if (action === 'updateConversationContext' && data) {
        const updateConversationContextData: ForethoughtData<'updateConversationContext'> =
          data;
        window.postMessage({
          event: 'updateConversationContext',
          payload: updateConversationContextData,
        });
      }

      if (action === 'proactiveTrigger' && data) {
        const proactiveTriggerData = data as ProactiveTriggerEvent;

        const { eventName, parameters } = proactiveTriggerData || {};

        if (!eventName) {
          console.error(
            "Invalid custom event: the payload must include a 'eventName' field",
          );
        }

        window.postMessage({
          action: 'proactiveTrigger',
          payload: { eventName, parameters },
        });
      }

      if (action === 'updateConfigParams' && data) {
        const updateConfigParamsData: ForethoughtData<'updateConfigParams'> =
          data;

        window.postMessage({
          event: 'updateConfigParams',
          payload: updateConfigParamsData,
        });
      }

      if (action === 'updatePositionParams') {
        // not applicable
      }

      if (action === 'sendMessage' && data) {
        const sendMessageData = data as ForethoughtData<'sendMessage'>;
        window.postMessage({
          action: 'sendMessage',
          payload: sendMessageData.payload,
        });
      }
    };
  }, []);
};
